/**
 * @function CallButton
 * @description Component for calling a participant.
 * @returns {JSX.Element | null} - The CallButton component.
 */

import { Popover, Transition } from '@headlessui/react'
import { Tooltip } from 'react-tooltip'
import CallIcon from '../assets/call.svg'
import { CallPopup } from './CallPopup'
import { RegularButton } from './elements/RegularButton'

/**
 * @interface CallButtonProps
 * @description Props for the CallButton component.
 * @property {string} participantId - The ID of the participant.
 */
interface CallButtonProps {
    participantId: string
}

/**
 * @function CallButton
 * @description Component for calling a participant.
 * @returns {JSX.Element | null} - The CallButton component.
 */
export default function CallButton({
    disabled,
    participantId,
}: CallButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
    return (
        <div className="flex z-50 rounded-md h-10" id="call-button">
            <Popover as="div" className="relative -ml-px block rounded-md">
                {() => (
                    <>
                        <Popover.Button
                            as={RegularButton}
                            disabled={disabled}
                            className="inline-flex items-center rounded-md border enabled:shadown-sm h-10 px-2.5 py-2.5 text-sm font-medium focus:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 enabled:active:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                        >
                            <CallIcon
                                className={`w-5 h-5 ${
                                    disabled ? 'fill-gray-400' : 'fill-indigo-600'
                                }`}
                            />
                            <span className="pl-2">Call</span>
                        </Popover.Button>
                        <Transition
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Popover.Panel className="absolute left-0 mt-2 -mr-1 w-[280px] origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                <CallPopup participantId={participantId} />
                            </Popover.Panel>
                        </Transition>
                    </>
                )}
            </Popover>
            {disabled && (
                <Tooltip anchorSelect="#call-button">
                    <p className="w-40 text-xs">
                        Provide phone number for this participant to be able to call them via
                        Dashboard.
                    </p>
                </Tooltip>
            )}
        </div>
    )
}
